// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.6.10-6UolF5LZuIaZoDlY8wmT/mode=imports,min/optimized/keen-slider.js'

const { setInitialised } = window.BAO.Carousel.plugins

class CardCarousel extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    if (!this.els?.slide?.exists) return

    if (window.BAO.MediaBreakpoints.isXsViewport && this.shouldMobileCarousel) {
      this.carousel = this.setupCarousel()
    } else if (window.BAO.MediaBreakpoints.isSm && this.shouldDesktopCarousel) {
      this.carousel = this.setupCarousel()
    }
  }

  setupCarousel () {
    this.onPreviousClick = this.onPreviousClick.bind(this)
    this.onNextClick = this.onNextClick.bind(this)
    this.onSlideChanged = this.onSlideChanged.bind(this)
    this.onSliderCreated = this.onSliderCreated.bind(this)

    this.classUp()

    const customPerView = this.dataset.perView || 3
    const slider = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        range: {
          max: this.els.slide.elements.length - 1,
        },
        breakpoints: {
          [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.sm}px)`]: {
            slides: {
              perView: customPerView,
              spacing: 10,
            },
          },
          [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.md}px)`]: {
            slides: {
              perView: customPerView,
              spacing: 20,
            },
          },
        },
        slides: {
          perView: 2.25,
          spacing: 10,
          number: this.els.slide.elements.length,
        },
        slideChanged: this.onSlideChanged,
        created: this.onSliderCreated,
      },
      [setInitialised()],
    )

    if (this.els?.previous?.element) {
      this.listeners.add(
        this.els.previous.element,
        'click',
        this.onPreviousClick,
      )
    }

    if (this.externalEls?.previous?.element) {
      this.listeners.add(
        this.externalEls.previous.element,
        'click',
        this.onPreviousClick,
      )
    }

    if (this.els?.next?.element) {
      this.listeners.add(this.els.next.element, 'click', this.onNextClick)
    }

    if (this.externalEls?.next?.element) {
      this.listeners.add(
        this.externalEls.next.element,
        'click',
        this.onNextClick,
      )
    }

    return slider
  }

  classUp () {
    if (!this.els.slides.element.classList.contains('keen-slider')) {
      this.els.slides.element.classList.add('keen-slider')
    }

    if (!this.els.slide.element.classList.contains('keen-slider__slide')) {
      this.els.slide.elements.forEach(el =>
        el.classList.add('keen-slider__slide'),
      )
    }
  }

  setCurrentPage () {
    if (!this.externalEls?.pages?.element) return

    this.externalEls.pages.element.textContent = `${this.currentPage}/${this.totalPages}`
  }

  onSlideChanged (instance) {
    const currentSlide = instance.track.details.abs
    const slideDetails = instance.track.details

    this.currentPage = Math.ceil((currentSlide + 1) / this.perPage)

    this.previousDisabled = currentSlide === 0

    const atKeenSliderMax = Math.abs(currentSlide - slideDetails.max) < 0.1
    const atLastSlideIndex = currentSlide >= this.slideCount - 1

    this.nextDisabled = atKeenSliderMax || atLastSlideIndex

    this.setCurrentPage()
  }

  onSliderCreated () {
    this.setAttribute('active', '')
    this.parent?.setAttribute(`${this.localName}-active`, '')

    this.setCurrentPage()
  }

  onNextClick () {
    if (!this.carousel) return

    const currentSlide = this.carousel.track.details.abs
    const maxSlide = this.slideCount - 1
    const nextSlide = Math.min(currentSlide + 1, maxSlide)

    this.carousel.moveToIdx(nextSlide, true)

    setTimeout(() => {
      const newCurrentSlide = this.carousel.track.details.abs
      const actuallyMoved = newCurrentSlide !== currentSlide

      this.nextDisabled =
        !actuallyMoved || newCurrentSlide >= this.slideCount - 1
      this.previousDisabled = newCurrentSlide === 0
    }, 100)
  }

  onPreviousClick () {
    if (!this.carousel) return

    const currentSlide = this.carousel.track.details.abs
    const prevSlide = Math.max(currentSlide - 1, 0)

    this.carousel.moveToIdx(prevSlide, true)

    setTimeout(() => {
      const newCurrentSlide = this.carousel.track.details.abs
      this.nextDisabled = newCurrentSlide >= this.slideCount - 1
      this.previousDisabled = newCurrentSlide === 0
    }, 50)
  }

  get previousDisabled () {
    return this._previousDisabled
  }

  set previousDisabled (val) {
    this._previousDisabled = val

    if (this.els?.previous?.element) {
      this.els.previous.element.disabled = val
    }

    if (this.externalEls?.previous?.element) {
      this.externalEls.previous.element.disabled = val
    }
  }

  get nextDisabled () {
    return this._nextDisabled
  }

  set nextDisabled (val) {
    this._nextDisabled = val

    if (this.els?.next?.element) {
      this.els.next.element.disabled = val
    }

    if (this.externalEls?.next?.element) {
      this.externalEls.next.element.disabled = val
    }
  }

  get currentPage () {
    return this._currentPage || 1
  }

  set currentPage (val) {
    if (val <= 0) {
      val = 1
    } else if (val > this.totalPages) {
      val = this.totalPages
    }

    this._currentPage = val
  }

  get perPage () {
    return window.BAO.MediaBreakpoints.isXsViewport ? 2 : 4
  }

  get totalPages () {
    return Math.ceil(this.slideCount / this.perPage)
  }

  get parentSelector () {
    return `[data-${this.identifier}-parent]`
  }

  get parent () {
    return this.closest(this.parentSelector)
  }

  get slideCount () {
    return this.els.slide.elements.length
  }

  get shouldMobileCarousel () {
    return this.slideCount > 2
  }

  get shouldDesktopCarousel () {
    return this.slideCount >= 4
  }
}

if (!customElements.get('card-carousel')) {
  customElements.define('card-carousel', CardCarousel)
}
